/**
 * src/components/analytics/OutboundLinkTracker.tsx
 *
 * Global click listener — site dışına giden link'leri (`<a href="https://...">`
 * with different host) otomatik GA4 `outbound_link_click` event olarak yakalar.
 *
 * Layout'a mount edilir (her sayfada bir kez). Window-level event listener
 * delegated — performance dostu (her link'e ayrı listener değil).
 *
 * Yakalanan:
 *   - http(s) cross-host: outbound_link_click { link_url, link_text, link_domain }
 *   - mailto:, tel:, sms: → atla (form_click veya zaten tel_click tracked)
 *   - Same-host link → atla (internal navigation, page_view zaten otomatik)
 *
 * Consent: gtag yoksa trackEvent no-op (PageViewEvent ile aynı pattern).
 */
'use client'

import { useEffect } from 'react'

import { trackEvent } from '@/lib/analytics/gtag'

export function OutboundLinkTracker() {
  useEffect(() => {
    if (typeof window === 'undefined') return

    const handler = (e: MouseEvent) => {
      // En yakın <a> ata; nested element (span, icon) içinden tıklanırsa bul.
      const target = e.target
      if (!(target instanceof Element)) return
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null
      if (!anchor) return

      const href = anchor.href
      if (!href || !/^https?:\/\//i.test(href)) return

      let url: URL
      try {
        url = new URL(href)
      } catch {
        return
      }

      // Aynı host → internal link, atla
      if (url.host === window.location.host) return

      trackEvent('outbound_link_click', {
        link_url: url.href,
        link_domain: url.host,
        link_text: (anchor.textContent ?? '').trim().slice(0, 100),
      })
    }

    // capture:false default — element click handler'ları önce çalışır,
    // sonra biz delegated handler ile yakalarız (passive: true performance hint)
    document.addEventListener('click', handler, { passive: true })
    return () => document.removeEventListener('click', handler)
  }, [])

  return null
}
